import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { useAppAuth } from '@/contexts/app-auth.context'
import { PageHeader } from '@/components/layout/PageHeader'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  getApiKeysClient,
  type ApiKeyListItem,
  type CreateApiKeyResponse,
} from '@/lib/api/api-keys.client'
import {
  ALL_PUBLIC_API_SCOPES,
  isProxyScope,
  ProxyIntegrationVendor,
  PublicApiScope,
  VENDOR_FOR_PROXY_SCOPE,
} from '@super/shared-types'
import { ROUTE_REGISTRY } from '@/lib/routes'
import { Copy, Eye, EyeOff, Key } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'

const SCOPE_LABELS: Record<PublicApiScope, string> = {
  [PublicApiScope.INVOCATIONS_WRITE]: 'Publish events (invocations:write)',
  [PublicApiScope.INVOCATIONS_READ]: 'Read invocations (invocations:read)',
  [PublicApiScope.CALLS_READ]: 'Read calls (calls:read)',
  [PublicApiScope.AGENTS_WRITE]: 'Manage agents (agents:write)',
  [PublicApiScope.AGENTS_READ]: 'Read agents (agents:read)',
  [PublicApiScope.TRIGGERS_WRITE]: 'Manage triggers (triggers:write)',
  [PublicApiScope.TRIGGERS_READ]: 'Read triggers (triggers:read)',
  [PublicApiScope.WEBHOOKS_WRITE]: 'Manage webhooks (webhooks:write)',
  [PublicApiScope.WEBHOOKS_READ]: 'Read webhooks (webhooks:read)',
  [PublicApiScope.COMPANY_READ]: 'Read company info (company:read)',
  [PublicApiScope.INTEGRATIONS_HUBSPOT_PROXY]:
    'Call HubSpot on behalf of your connected portal',
  [PublicApiScope.INTEGRATIONS_BUILDIUM_PROXY]:
    'Call Buildium on behalf of your connected account',
}

const VENDOR_DISPLAY_NAME: Record<ProxyIntegrationVendor, string> = {
  [ProxyIntegrationVendor.HUBSPOT]: 'HubSpot',
  [ProxyIntegrationVendor.BUILDIUM]: 'Buildium',
}

function vendorsFromScopes(scopes: readonly PublicApiScope[]): string[] {
  const vendors = new Set<ProxyIntegrationVendor>()
  for (const scope of scopes) {
    const vendor = VENDOR_FOR_PROXY_SCOPE.get(scope)
    if (vendor) vendors.add(vendor)
  }
  return Array.from(vendors).map(v => VENDOR_DISPLAY_NAME[v])
}

const formatDate = (dateString: string | null) => {
  if (!dateString) return 'Never'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const hasLastFour = (key: ApiKeyListItem) => key.keyLastFour !== null

function ScopeRow({
  scope,
  checked,
  onToggle,
}: {
  scope: PublicApiScope
  checked: boolean
  onToggle: (scope: PublicApiScope) => void
}) {
  const id = `scope-${scope}`
  return (
    <div className="flex items-center space-x-2">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={() => onToggle(scope)}
      />
      <Label htmlFor={id} className="mb-0 font-normal cursor-pointer">
        {SCOPE_LABELS[scope] ?? scope}
      </Label>
    </div>
  )
}

function SectionHeader({
  title,
  scopes,
  selectedScopes,
  setSelectedForSection,
}: {
  title: string
  scopes: readonly PublicApiScope[]
  selectedScopes: Set<PublicApiScope>
  setSelectedForSection: (
    sectionScopes: readonly PublicApiScope[],
    checked: boolean,
  ) => void
}) {
  const allChecked = scopes.every(s => selectedScopes.has(s))
  return (
    <div className="flex items-center justify-between px-3">
      <Label className="mb-0">{title}</Label>
      <button
        type="button"
        className="text-xs text-muted-foreground hover:text-foreground"
        onClick={() => setSelectedForSection(scopes, !allChecked)}
      >
        {allChecked ? 'Clear all' : 'Select all'}
      </button>
    </div>
  )
}

function renderScopeSection({
  title,
  scopes,
  selectedScopes,
  toggleScope,
  setSelectedForSection,
}: {
  title: string
  scopes: readonly PublicApiScope[]
  selectedScopes: Set<PublicApiScope>
  toggleScope: (scope: PublicApiScope) => void
  setSelectedForSection: (
    sectionScopes: readonly PublicApiScope[],
    checked: boolean,
  ) => void
}) {
  return (
    <div className="grid gap-1 mb-6">
      <SectionHeader
        title={title}
        scopes={scopes}
        selectedScopes={selectedScopes}
        setSelectedForSection={setSelectedForSection}
      />
      <div className="space-y-2 rounded-md border border-input p-3">
        {scopes.map(scope => (
          <ScopeRow
            key={scope}
            scope={scope}
            checked={selectedScopes.has(scope)}
            onToggle={toggleScope}
          />
        ))}
      </div>
    </div>
  )
}

function renderProxyScopeSection({
  grantableScopes,
  selectedScopes,
  toggleScope,
  setSelectedForSection,
}: {
  grantableScopes: PublicApiScope[] | null
  selectedScopes: Set<PublicApiScope>
  toggleScope: (scope: PublicApiScope) => void
  setSelectedForSection: (
    sectionScopes: readonly PublicApiScope[],
    checked: boolean,
  ) => void
}) {
  const grantableProxyScopes = grantableScopes?.filter(isProxyScope) ?? []
  return (
    <div className="grid gap-1 mb-6">
      {grantableProxyScopes.length > 0 ? (
        <SectionHeader
          title="API-level data access"
          scopes={grantableProxyScopes}
          selectedScopes={selectedScopes}
          setSelectedForSection={setSelectedForSection}
        />
      ) : (
        <Label className="px-3">API-level data access</Label>
      )}
      {grantableScopes === null ? (
        <div className="rounded-md border border-input p-3">
          <Skeleton className="h-4 w-64" />
        </div>
      ) : grantableProxyScopes.length === 0 ? (
        <div className="rounded-md border border-dashed border-input p-3 text-sm text-muted-foreground">
          Connect an integration to grant API-level data access on this key.{' '}
          <Link
            to={ROUTE_REGISTRY.SETTINGS_INTEGRATIONS.path}
            className="underline underline-offset-2"
          >
            Manage integrations
          </Link>
        </div>
      ) : (
        <>
          <div className="space-y-2 rounded-md border border-input p-3">
            {grantableProxyScopes.map(scope => (
              <ScopeRow
                key={scope}
                scope={scope}
                checked={selectedScopes.has(scope)}
                onToggle={toggleScope}
              />
            ))}
          </div>
          <p className="text-muted-foreground text-xs ml-3">
            These scopes let this key call Buildium and HubSpot on your
            connected account. Grant to automations you trust.
          </p>
        </>
      )}
    </div>
  )
}

export function ApiKeysSettingsPage() {
  const { currentCompany, isSuperAdmin, isInitialized } = useAppAuth()
  const [apiKeys, setApiKeys] = useState<ApiKeyListItem[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [newKeyDialogOpen, setNewKeyDialogOpen] = useState(false)
  const [newKey, setNewKey] = useState<CreateApiKeyResponse | null>(null)
  const [showNewKey, setShowNewKey] = useState(false)
  const [name, setName] = useState('')
  const [grantableScopes, setGrantableScopes] = useState<
    PublicApiScope[] | null
  >(null)
  const [selectedScopes, setSelectedScopes] = useState<Set<PublicApiScope>>(
    () => new Set(ALL_PUBLIC_API_SCOPES),
  )

  const selectedProxyScopes = useMemo(
    () => Array.from(selectedScopes).filter(isProxyScope),
    [selectedScopes],
  )
  const hasElevatedScopeSelected = selectedProxyScopes.length > 0

  useEffect(() => {
    if (!isInitialized) return
    if (isSuperAdmin && !currentCompany?.id) return
    loadApiKeys()
  }, [currentCompany?.id, isSuperAdmin, isInitialized])

  const loadGrantableScopes = async () => {
    try {
      let scopes: PublicApiScope[]
      if (isSuperAdmin && currentCompany?.id) {
        const client = getApiKeysClient(true)
        scopes = await client.getGrantableScopes(currentCompany.id)
      } else {
        const client = getApiKeysClient(false)
        scopes = await client.getGrantableScopes()
      }
      setGrantableScopes(scopes)
      // Default: platform scopes checked, proxy scopes unchecked.
      setSelectedScopes(new Set(scopes.filter(s => !isProxyScope(s))))
    } catch (error) {
      // Fall back to the safe default set so the dialog is still usable.
      setGrantableScopes([...ALL_PUBLIC_API_SCOPES])
      setSelectedScopes(new Set(ALL_PUBLIC_API_SCOPES))
    }
  }

  const loadApiKeys = async () => {
    try {
      setLoading(true)

      let keys: ApiKeyListItem[]
      if (isSuperAdmin && currentCompany?.id) {
        const client = getApiKeysClient(true)
        keys = await client.listApiKeys(currentCompany.id)
      } else {
        const client = getApiKeysClient(false)
        keys = await client.listApiKeys()
      }

      setApiKeys(Array.isArray(keys) ? keys : [])
    } catch (error) {
      if (error instanceof Error) {
        toast.error(`Failed to load API keys: ${error.message}`)
      } else {
        toast.error('Failed to load API keys')
      }
      setApiKeys([])
    } finally {
      setLoading(false)
    }
  }

  const handleOpenCreateDialog = () => {
    setNewKeyDialogOpen(true)
    // Refresh grantable scopes each time the dialog opens so a just-connected
    // integration shows up as a checkable proxy scope without a page reload.
    void loadGrantableScopes()
  }

  const toggleScope = (scope: PublicApiScope) => {
    setSelectedScopes(prev => {
      const next = new Set(prev)
      if (next.has(scope)) next.delete(scope)
      else next.add(scope)
      return next
    })
  }

  const setSelectedForSection = (
    sectionScopes: readonly PublicApiScope[],
    checked: boolean,
  ) => {
    setSelectedScopes(prev => {
      const next = new Set(prev)
      for (const scope of sectionScopes) {
        if (checked) next.add(scope)
        else next.delete(scope)
      }
      return next
    })
  }

  const handleCreateApiKey = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name for the API key')
      return
    }
    try {
      setCreating(true)
      const scopes = Array.from(selectedScopes)
      let created: CreateApiKeyResponse
      if (isSuperAdmin && currentCompany?.id) {
        const client = getApiKeysClient(true)
        created = await client.createApiKey(currentCompany.id, {
          name: name.trim(),
          scopes,
        })
      } else {
        const client = getApiKeysClient(false)
        created = await client.createApiKey({ name: name.trim(), scopes })
      }

      if (!created || !created.apiKey) {
        toast.error('API key was not returned. Please try again.')
        return
      }

      setNewKey(created)
      setShowNewKey(false)
      await loadApiKeys()
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to create API key. Please try again.'
      toast.error(errorMessage)
    } finally {
      setCreating(false)
    }
  }

  const handleDeleteApiKey = async (id: string) => {
    if (
      !confirm(
        'Are you sure you want to revoke this API key? This action cannot be undone.',
      )
    ) {
      return
    }

    try {
      if (isSuperAdmin && currentCompany?.id) {
        const client = getApiKeysClient(true)
        await client.deleteApiKey(id, currentCompany.id)
      } else {
        const client = getApiKeysClient(false)
        await client.deleteApiKey(id)
      }
      await loadApiKeys()
      toast.success('API key revoked successfully')
    } catch (error) {
      toast.error('Failed to revoke API key')
    }
  }

  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key)
    toast.success('API key copied to clipboard')
  }

  const handleCloseNewKeyDialog = () => {
    setNewKeyDialogOpen(false)
    setNewKey(null)
    setShowNewKey(false)
    setName('')
  }

  if (!isInitialized) {
    return null
  }

  return (
    <div className="h-full overflow-y-auto mt-16">
      <FloatingActionIslandProvider>
        <div className="space-y-6 p-6 pt-0">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mt-6">
            <PageHeader title="Manage API keys" />
            <Button
              onClick={handleOpenCreateDialog}
              variant="form"
              size="sm"
              className="h-9 w-fit shrink-0"
            >
              Create API key
            </Button>
          </div>

          <Card className="w-full">
            <CardContent className="p-0">
              {loading || apiKeys === null ? (
                <div className="space-y-0">
                  <div className="border-b">
                    <div className="flex space-x-4 px-6 py-3.5">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                  </div>
                  <div className="divide-y">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="flex space-x-4 px-6 py-4">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-4 w-28" />
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-4 w-20" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : apiKeys.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <Key className="h-8 w-8 text-gray-400 mb-4" />
                  <h3 className="mb-2 text-lg font-semibold">
                    No API keys found
                  </h3>
                  <p className="mb-6 max-w-sm text-sm text-muted-foreground">
                    Generate a new API key to authenticate requests to our
                    internal services. Store these securely—it will only be
                    displayed once during generation.
                  </p>
                  <Button onClick={handleOpenCreateDialog}>
                    Create your first API key
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Key name</TableHead>
                      <TableHead>Key</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="w-[180px]" align="center">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {apiKeys &&
                      apiKeys.map(key => (
                        <TableRow key={key.id}>
                          <TableCell className="font-medium">
                            {key.name}
                          </TableCell>
                          <TableCell>
                            {!hasLastFour(key) ? (
                              <Badge
                                variant="outline"
                                title="Older key format — create a new sk_super_ key for Public API access"
                              >
                                Legacy
                              </Badge>
                            ) : (
                              <span className="font-mono text-xs text-muted-foreground">
                                …{key.keyLastFour}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>{formatDate(key.createdAt)}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteApiKey(key.id)}
                              >
                                Revoke
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <FloatingActionIsland />
        </div>
      </FloatingActionIslandProvider>

      <Dialog open={newKeyDialogOpen} onOpenChange={setNewKeyDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          {!newKey ? (
            <>
              <DialogHeader>
                <DialogTitle>Create an API key</DialogTitle>
                <DialogDescription>
                  Give your key a name that describes what it does.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid gap-1 mb-6">
                  <Label htmlFor="name" className="px-3">
                    Name
                  </Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="For example: Production API Key"
                  />
                </div>
                {renderScopeSection({
                  title: 'Platform access',
                  scopes: ALL_PUBLIC_API_SCOPES,
                  selectedScopes,
                  toggleScope,
                  setSelectedForSection,
                })}
                {renderProxyScopeSection({
                  grantableScopes,
                  selectedScopes,
                  toggleScope,
                  setSelectedForSection,
                })}
                {hasElevatedScopeSelected && (
                  <div className="mb-6 rounded-md border border-input bg-muted/50 p-3">
                    <p className="text-xs text-foreground">
                      You're about to grant API-level data access to:{' '}
                      {vendorsFromScopes(selectedProxyScopes).join(', ')}.
                    </p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={handleCloseNewKeyDialog}
                  disabled={creating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateApiKey}
                  disabled={
                    creating || !name.trim() || selectedScopes.size === 0
                  }
                >
                  {creating ? 'Creating...' : 'Create'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Copy your API key</DialogTitle>
                <DialogDescription>
                  Copy your API key and save it somewhere safe. You can't
                  recover the API key after you're done with this step.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid gap-1 mb-6">
                  <Label className="px-3">API key</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type={showNewKey ? 'text' : 'password'}
                      value={newKey.apiKey}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowNewKey(!showNewKey)}
                      className="shrink-0"
                    >
                      {showNewKey ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        newKey?.apiKey && handleCopyKey(newKey.apiKey)
                      }
                      disabled={!newKey?.apiKey}
                      className="shrink-0"
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </Button>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleCloseNewKeyDialog}>Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
